/**
 * Shared sessionStorage key + payload used to hand an Agent Connection
 * setup off from the dashboard Connections > New view to the file route.
 * The dashboard cannot drive the AI conversation directly because
 * `useSubmitAIAnalystPrompt` requires a valid `fileUuid` — instead it
 * persists the seed here and navigates to file creation. The file shell
 * reads + clears this on first render after `fileUuid` is available and
 * submits a seeded user prompt that asks the AI to start the setup.
 *
 * Versioned so we can evolve the payload without breaking pre-existing
 * tabs that picked up an older shape.
 */
export const PENDING_AGENT_SETUP_KEY = 'quadratic.pendingAgentSetup';

/** Legacy key (kept for one release so old tabs in flight still work). */
export const PENDING_CUSTOM_AGENT_SETUP_KEY = 'quadratic.pendingCustomAgentSetup';

export type PendingAgentSetupPayload =
  | {
      v: 2;
      teamUuid: string;
      kind: 'catalog';
      catalogSlug: string;
      displayName: string;
    }
  | {
      v: 2;
      teamUuid: string;
      kind: 'custom';
      /** Service name the user typed into the dashboard search box, if any. */
      initialServiceName?: string;
    }
  | {
      v: 2;
      teamUuid: string;
      /**
       * Open-ended "Connect to anything" flow. Mirrors the in-grid Add
       * Connection menu's button: pushes an informational welcome bubble
       * into the chat (filtered from the AI request) and sets the one-shot
       * `pendingAddConnectionIntentAtom` so the next user message carries
       * `Context.addConnectionIntent`, which `getMessagesForAI` translates
       * into a NOTE telling the model to start an agent-connection setup.
       * With a `serviceName` we submit it immediately; without one we just
       * focus the empty chat input.
       */
      kind: 'connect-to-anything';
      serviceName?: string;
    };

/** Back-compat shape for tabs that wrote the v1 payload before this update. */
export type PendingCustomAgentSetupPayload = {
  v: 1;
  teamUuid: string;
  initialServiceName?: string;
};

export const isPendingAgentSetupPayload = (value: unknown): value is PendingAgentSetupPayload => {
  if (!value || typeof value !== 'object') return false;
  const v = value as Record<string, unknown>;
  if (v.v !== 2 || typeof v.teamUuid !== 'string') return false;
  if (v.kind === 'catalog') {
    return typeof v.catalogSlug === 'string' && typeof v.displayName === 'string';
  }
  if (v.kind === 'custom') {
    return v.initialServiceName === undefined || typeof v.initialServiceName === 'string';
  }
  if (v.kind === 'connect-to-anything') {
    return v.serviceName === undefined || typeof v.serviceName === 'string';
  }
  return false;
};

export const isPendingCustomAgentSetupPayload = (value: unknown): value is PendingCustomAgentSetupPayload => {
  if (!value || typeof value !== 'object') return false;
  const v = value as Record<string, unknown>;
  return (
    v.v === 1 &&
    typeof v.teamUuid === 'string' &&
    (v.initialServiceName === undefined || typeof v.initialServiceName === 'string')
  );
};

/**
 * Non-consuming peek: returns true when a valid pending agent setup payload
 * is currently in sessionStorage. Used by the file shell to suppress the
 * "this file is empty" onboarding overlay so it doesn't cover the AI chat
 * that the consume hook is about to drive.
 */
export const hasPendingAgentSetup = (): boolean => {
  let raw: string | null = null;
  try {
    raw = sessionStorage.getItem(PENDING_AGENT_SETUP_KEY);
  } catch {
    return false;
  }
  if (!raw) return false;
  try {
    return isPendingAgentSetupPayload(JSON.parse(raw));
  } catch {
    return false;
  }
};
